import { Button as ButtonPrimitive } from "@base-ui/react/button";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/* eslint-disable react-refresh/only-export-components -- shadcn co-locates the variant helper with its component */

const buttonVariants = cva(
  "cc-control group/button inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md border font-sans text-sm font-semibold outline-none select-none transition-[color,background-color,border-color,box-shadow,opacity] duration-150 ease-out focus-visible:shadow-[var(--focus-ring)] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-45 aria-invalid:border-destructive aria-invalid:shadow-[var(--field-focus-shadow)] [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: "border-transparent bg-primary text-primary-foreground hover:bg-primary-hover",
        outline:
          "border-input bg-card text-foreground hover:border-primary hover:text-link aria-expanded:border-primary aria-expanded:text-link",
        secondary:
          "border-transparent bg-muted text-foreground hover:bg-[color-mix(in_srgb,var(--surface-2)_80%,var(--line))]",
        ghost:
          "border-transparent bg-transparent text-foreground shadow-none hover:bg-muted hover:text-foreground aria-expanded:bg-muted aria-expanded:text-foreground",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground hover:bg-destructive-deep",
        link: "border-transparent text-link shadow-none underline-offset-4 hover:underline",
      },
      size: {
        default:
          "min-h-11 gap-2 px-[18px] py-2 has-data-[icon=inline-end]:pr-4 has-data-[icon=inline-start]:pl-4",
        xs: "min-h-8 gap-1.5 px-2.5 py-1 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        sm: "min-h-9 gap-1.5 px-3 py-1.5 text-sm [&_svg:not([class*='size-'])]:size-4",
        lg: "min-h-12 gap-2 px-[26px] py-3 text-base",
        icon: "size-11 p-0",
        "icon-xs": "size-8 p-0 [&_svg:not([class*='size-'])]:size-3.5",
        "icon-sm": "size-9 p-0",
        "icon-lg": "size-12 p-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

function Button({
  className,
  variant = "default",
  size = "default",
  ...props
}: ButtonPrimitive.Props & VariantProps<typeof buttonVariants>) {
  return (
    <ButtonPrimitive
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
